/**
 * Trial Service — Manages trial lifecycle state calculations.
 *
 * The trial period starts when the user first signs in (createdAt timestamp
 * on their UserProfile). This service provides pure functions that compute
 * the current trial state from the start date and current time.
 *
 * State Machine:
 *   FIRST_SIGN_IN → TRIAL_ACTIVE (Days 1-5)
 *                 → TRIAL_URGENCY (Days 6-7)
 *                 → TRIAL_EXPIRED (Day 8+)
 *                 → PLAN_SELECTED (user chose Basic/Advanced)
 *                 → FREE_TIER (user declined all plans)
 *
 * All calculations use UTC to avoid timezone and DST edge cases.
 */

/** Total number of trial days (inclusive). */
export const TRIAL_DURATION_DAYS = 7;

/** Day on which urgency messaging begins. */
const URGENCY_START_DAY = 6;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Trial lifecycle states.
 */
export const TrialState = {
  /** Days 1-5: Normal trial, dismissible banner */
  TRIAL_ACTIVE: "TRIAL_ACTIVE",
  /** Days 6-7: Urgent trial, prominent non-dismissible banner */
  TRIAL_URGENCY: "TRIAL_URGENCY",
  /** Day 8+: Trial expired, blocking modal required */
  TRIAL_EXPIRED: "TRIAL_EXPIRED",
  /** User selected a paid plan */
  PLAN_SELECTED: "PLAN_SELECTED",
  /** User chose free tier after trial expiry */
  FREE_TIER: "FREE_TIER",
} as const;

export type TrialState = (typeof TrialState)[keyof typeof TrialState];

/**
 * Get the current trial day (1-based).
 * Day 1 = signup day. Partial elapsed days are counted as the next full day.
 */
export function getTrialDay(trialStartDate: Date): number {
  const now = Date.now();
  const elapsed = now - trialStartDate.getTime();
  const fullDaysElapsed = Math.floor(elapsed / MS_PER_DAY);
  return fullDaysElapsed + 1;
}

/**
 * Get the number of days remaining in the trial (minimum 0).
 */
export function getDaysRemaining(trialStartDate: Date): number {
  const day = getTrialDay(trialStartDate);
  return Math.max(0, TRIAL_DURATION_DAYS - day + 1);
}

/**
 * Whether the trial is still active (Days 1-7 inclusive).
 */
export function isTrialActive(trialStartDate: Date): boolean {
  return getTrialDay(trialStartDate) <= TRIAL_DURATION_DAYS;
}

/**
 * Whether the trial is in the urgency period (Days 6-7).
 */
export function isTrialUrgent(trialStartDate: Date): boolean {
  const day = getTrialDay(trialStartDate);
  return day >= URGENCY_START_DAY && day <= TRIAL_DURATION_DAYS;
}

/**
 * Whether the trial has expired (Day 8+).
 */
export function isTrialExpired(trialStartDate: Date): boolean {
  return getTrialDay(trialStartDate) > TRIAL_DURATION_DAYS;
}

/**
 * Get the current trial lifecycle state from the start date.
 * Does not account for PLAN_SELECTED or FREE_TIER — those are user actions
 * stored externally (Firestore).
 */
export function getTrialStatus(trialStartDate: Date): TrialState {
  const day = getTrialDay(trialStartDate);

  if (day > TRIAL_DURATION_DAYS) {
    return TrialState.TRIAL_EXPIRED;
  }

  if (day >= URGENCY_START_DAY) {
    return TrialState.TRIAL_URGENCY;
  }

  return TrialState.TRIAL_ACTIVE;
}
